import { IllustrationOpenSpace } from "@assets/IllustrationOpenSpace";
import { IllustrationWrongSide } from "@assets/IllustrationWrongSide";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { HubScannerDrawer } from "~/components/HubScannerDrawer";
import { ThaliaHeader } from "~/components/layout/ThaliaHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { useHubSession } from "@contexts/HubSessionContext";
import { useLocation } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { AppHeader } from "~/components/layout/AppHeader";

type OpenDoorPageProps = {
	hubId: string;
	hubName: string;
	side: string | undefined;
	onOpenDoor: () => void;
	isLoading?: boolean;
	isSncPickup?: boolean;
	isThaliaPickup?: boolean;
	/** When true, skip hub session check and make the open door button always clickable (e.g. instant-buy flow where the hub is already known) */
	skipHubCheck?: boolean;
};

export const OpenDoorPage = (props: OpenDoorPageProps) => {
	const { hubId, hubName, side, onOpenDoor, isLoading = false, isSncPickup, isThaliaPickup, skipHubCheck } = props;
	const { hub, setHub } = useHubSession();
	const i18n = useI18nContext().LL["new-ui"];
	const location = useLocation();

	// Check if we need to scan the hub
	// Need to scan if: no hub, wrong hub, or if we need a specific side but don't have it or have wrong side
	const needsToScanHub = !skipHubCheck && (!hub || hub.id !== hubId || (side && (!hub.side || hub.side !== side)));
	// Show wrong side message only if hub is correct but side is wrong
	const isAtWrongSide = !skipHubCheck && side && hub && hub.id === hubId && hub.side && hub.side !== side;

	const canOpendoor = !needsToScanHub && !isAtWrongSide;
	const showThaliaHeader = location.pathname.includes("clickncollect") && isThaliaPickup === true;

	const handleOpenDoorClick = () => {
		if (!canOpendoor || isLoading) {
			return;
		}

		onOpenDoor();
	};

	return (
		<>
			{showThaliaHeader ? <ThaliaHeader /> : null}
			<PageContainer
				className="px-6"
				footer={
					isAtWrongSide ? (
						<Button
							className="w-full"
							size="lg"
							onClick={() =>
								setHub({
									...hub,
									side: side as "A" | "B",
								})
							}
						>
							<T>{i18n.IM_ON_CORRECT_SIDE()}</T>
						</Button>
					) : (
						<Button className="w-full" size="lg" disabled={!canOpendoor || isLoading} onClick={handleOpenDoorClick}>
							{isLoading ? (
								<>
									{i18n.OPENING_DOOR_FOR_YOU()}
									<Loader2 className="size-4 animate-spin" />
								</>
							) : (
								i18n.OPEN_DOOR()
							)}
						</Button>
					)
				}
			>
				{!showThaliaHeader &&
					<div className="-mx-6">
						<AppHeader showHelpButton={true} />
					</div>
				}
				{isAtWrongSide ? (
					<>
						<h1 className="text-2xl font-breit">
							<T>{i18n.GO_CORRECT_SIDE_HUB().replace("#", `${hubName}-${side}`)}</T>
						</h1>
						<p className="text-sm text-muted-foreground">
							<ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
								<li>
									<T>{i18n.GO_OTHER_SIDE_HUB()}</T>
								</li>
								<li>
									<T>{i18n.GO_OTHER_SIDE_DESC()}</T>{" "}
									<span className="font-semibold text-neutral-900">
										{hubName}-{side}
									</span>
								</li>
							</ul>
						</p>
						<IllustrationWrongSide className="w-full h-[50vh]" />
					</>
				) : (
					<>
						<h1 className="text-2xl font-breit">
							<T>{i18n.TIME_TO_ENJOY()}</T>
						</h1>
						<p className="text-sm text-muted-foreground">
							<ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
								<li>
									<T>{i18n.TIME_TO_ENJOY_DESC()}</T>
								</li>
								<li>
									<T>{i18n.TIME_TO_ENJOY_DESC_REMEMBER()}</T>
								</li>
							</ul>
						</p>
						<IllustrationOpenSpace className="w-full h-[50vh]" />
						{!skipHubCheck && <HubScannerDrawer isOpen={!canOpendoor} onClose={() => { }} expectedHubId={hubId} expectedSide={side} />}
					</>
				)}
			</PageContainer>
		</>
	);
};
